import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  Field,
  FieldProps,
  useFormikContext,
  useField,
} from 'formik';
import classNames from 'classnames';
import ErrorMessage from 'hellospa/components/error-message';
import { useFeatureFlag } from 'hellospa/components/feature-flags';
import Checkbox from 'common/components/checkbox';
import TextField from 'hellospa/foundation/control/text-field';
import TextArea from 'hellospa/foundation/control/textarea';
import * as types from 'hellospa/page/prep-and-send/data/types';
import styles from '../index.scss';
import messages from './intl';
import Button from 'common/components/button';


type Props = {
  isAutoGenerated: boolean,
  save: () => void,
};

const Fields = ({ isAutoGenerated, save }: Props) => {
  const intl = useIntl();
  const extraAttachments = useFeatureFlag('HelloSign_Request_Additional_Attachments');
  const { errors } = useFormikContext();
  const [currentField] = useField<types.Attachment>('current');
  const [attachmentsField] = useField<types.Attachment[]>('attachments');
  const [, currentNameMeta] = useField<types.Attachment>('current.name');

  /**
   * Scan through all attachment fields and look for errors and generate a single
   * object. We must do it this way because we only display a single record's fields
   * at one time, but some errors like "unique" checks are attached to the first
   * instance of a record, so if a uniqueness check fails on records
   * 1 and 4 (the one the user is currently editing), the error would attach itself
   * to record 1 but the error would not be displayed because that's not the active
   * fields the user is editing!
   */
  const attachmentErrors = errors as Record<string, types.Attachment>;
  const allErrors = useMemo(() => Object.keys(attachmentErrors || {}).reduce(
    (acc, index) => ({
      ...acc,
      ...attachmentErrors[index],
    }), {} as Partial<types.Attachment>,
  ), [attachmentErrors]);

  const showFields = React.useCallback(() => {
    // Show fields if we're editing an existing record
    const isEditing = attachmentsField.value.find(
      (attachment) => currentField.value.id === attachment.id,
    );

    if (isEditing) {
      return true;
    }
    let maxAttachments = 5;
    if (extraAttachments) {
      maxAttachments = 15;
    }
    if (attachmentsField.value.length >= maxAttachments) {
      return false;
    }

    return true;
  }, [attachmentsField.value, currentField.value.id, extraAttachments]);

  if (!showFields()) {
    return null;
  }


  const validateUniqueName = async (value: string) => {
    const transformedValue = value.trim().toLowerCase();
    const isDuplicate = attachmentsField.value.some(
      (attachment: types.Attachment) => attachment.name.trim().toLowerCase() === transformedValue
        && currentField.value.id !== attachment.id,
    );

    return isDuplicate ? intl.formatMessage(messages.nameNotUniqueError) : null;
  };

  const submitButtonIsDisabled = currentField.value.name.trim() === '';

  return (
    <div className={classNames(styles.modalFields, styles.fields)}>
      <div className={styles.fieldRow}>
        <div
          className={classNames({
            [styles.errorFieldContainer]: currentNameMeta.touched && allErrors.name,
          })}
        >
          <label htmlFor="current.name">
            {intl.formatMessage(messages.fieldNameLabel)}
          </label>
          <Field
            name="current.name"
            validate={validateUniqueName}
          >
            {({ field }: FieldProps) => <TextField
              id="current.name"
              placeholder={intl.formatMessage(messages.fieldNamePlaceholder)}
              data-qa-ref="recipients.fields.attachments.name"
              {...field} />}
          </Field>
          <div
            className={styles.errorContainer}
            role="attachment-name-error"
            data-qa-ref="recipients.errors.attachments.name"
          >
            <ErrorMessage
              name='current.name'
              className={styles.error} />
          </div>
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div
          className={classNames({
            [styles.errorFieldContainer]: currentNameMeta.touched && allErrors.instructions,
          })}
        >
          <label htmlFor="current.instructions">
            {intl.formatMessage(messages.fieldInstructionsLabel)}
          </label>
          <Field
            name="current.instructions"
          >
            {({ field }: FieldProps) => <TextArea
              id="current.instructions"
              placeholder={intl.formatMessage(messages.fieldInstructionsPlaceholder)}
              data-qa-ref="recipients.fields.attachments.instructions"
              rows={10}
              {...field} />}
          </Field>
          <div
            className={styles.errorContainer}
            role="attachment-instructions-error"
            data-qa-ref="recipients.errors.attachments.instructions"
          >
            <ErrorMessage
              name='current.instructions'
              className={styles.error} />
          </div>
        </div>
      </div>

      <div className={styles.fieldRow}>
        <label>
          <Field name="current.required">
            {({ form, field }: FieldProps) => (
              <Checkbox
                id="recipients.fields.attachments.required"
                indeterminate={false}
                checked={field.value}
                disabled={false}
                className={styles.requiredFieldCheckbox}
                onChange={() => {
                  form.setFieldValue(field.name, !field.value);
                  form.setFieldTouched(field.name, true);
                }}
                data-qa-ref="recipients.fields.attachments.required" />
            )}
          </Field>
          {intl.formatMessage(messages.fieldRequiredLabel)}
        </label>
      </div>
      <Button
        data-qa-ref="recipients.buttons.attachments.add"
        role="button"
        buttonTextUppercase={false}
        disabled={submitButtonIsDisabled}
        onClick={save}
        buttonText={intl.formatMessage(isAutoGenerated
          ? messages.btnAdd
          : messages.btnUpdate)}
        buttonBorderColor={submitButtonIsDisabled ? '' : 'ocean-blue'}
        buttonTextColor="ocean-blue"
        buttonColor="white"
        buttonHoverColor="ocean-blue"
        buttonHoverTextColor="white" />

    </div>
  );
};

export default Fields;
